import { useState } from 'react';
import {
    Badge,
    BlockStack,
    Box,
    Card,
    DataTable,
    InlineStack,
    PageHeader,
    SegmentedControl,
    Select,
    Typography,
    vars,
    type DataTableColumn,
    type SegmentedOption,
    type SelectOption,
} from '@pv-frontend/ui-components-web';
import {
    ALL_DOMAINS,
    durationLabel,
    expiryLabel,
    filterRows,
    checkedLabel,
    isSeededOutcome,
    lastSuccessLabel,
    nextDueLabel,
    rowSubline,
    schedulerView,
    shortDate,
    shortDateTime,
    tickLabel,
    stripOrder,
    statusLook,
    type HealthReportView,
    type HealthRowView,
    type StatusFilter,
} from './data-health-view';

const dash = '—';

function stripTone(status: string, frozen: boolean): 'critical' | 'warning' | 'success' | 'neutral' | 'info' {
    if (frozen && status === 'GREEN') return 'info';
    if (status === 'RED') return 'critical';
    if (status === 'AMBER') return 'warning';
    if (status === 'GREEN') return 'success';
    return 'neutral';
}

/** Long unbroken text (a probe URL, an error message) must wrap, not widen its column. */
const wrapLongText = { overflowWrap: 'anywhere' } as const;

/** The detail column never gets less than this, so the others stay readable. */
const DETAIL_MIN_WIDTH = 160;

/**
 * ...and never more than this. Auto table layout shares spare width by content length, so without a cap a
 * long probe message takes it all (655 px at 1920, 2500 px at 3840) while the version columns wrap.
 */
const DETAIL_MAX_WIDTH = 560;

/**
 * Column minimums, each measured in the browser (headless Chrome, the font the admin uses) for the widest
 * value the column can hold, text only (the cell adds `CELL_PADDING` each side):
 *   Status        91   the widest badge, "Derived row" (badges wrap, so one at a time sets the minimum)
 *   Source/table 160   `DETAIL_MIN_WIDTH`
 *   Last success 145   "28 Sep 2025 · 9999 d" measures 144.2
 *   Next due      86   "28 Sep 2025" measures 85
 *   Duration      67   "99 h 59 m" measures 66.7
 *   Version held  71   its header, "Version held"; Latest published 95, its header (headers cannot wrap)
 * These are set as real `min-width`s on the cells, so a value that fits them can never push the table wider.
 */
const STATUS_WIDTH = 91;
const SUCCESS_WIDTH = 145;
const DUE_WIDTH = 86;
const DURATION_WIDTH = 67;
const CELL_PADDING = 32; // 16 px each side, from the DataTable cell
const HEADER_SKEW = 12; // ("Latest published" 95 - "Version held" 71) / 2
const TABLE_BORDER = 2; // the table shell's 1 px border, inside the container

/**
 * The version columns want to be wide enough for a whole source line to sit on one row (the longest real
 * line, `defra_noise_important_areas as-of 2026-10-11`, is 267 px of text, so 270; fe#1093), but only when
 * the table has the room. The table sits in a container (`TABLE_FRAME_STYLE`), so this is sized from the
 * table's own width in `cqw`, not the window's. The sum holds by construction:
 *   other five columns  91 + 160 + 145 + 86 + 67 = 549 text
 *   padding             7 cells x 32             = 224
 *   border                                       =   2
 *   reserved                                     = 775, half each = 387.5
 *   header skew         (95 - 71) / 2            =  12   (Latest published's header is wider, so it
 *                                                         takes up to 24 px more than Version held's)
 *   safety                                       =   2   -> 402
 * so each version cell is `50cqw - 402px` wide at most, which leaves the five others at least their
 * minimums and the two version columns at least their headers (71 + 95, the floor). That is why it never
 * forces a horizontal scroll, with or without a scrollbar gutter, and shrinks to nothing on a narrow table
 * (the line then wraps, `wrapLongText`). With every column at its worst case the table needs 941 px, a
 * window of about 1255 px (1270 with a 15 px gutter). A browser with no container-query units (Firefox 104
 * to 109) drops the declaration: the columns just wrap more, they never scroll.
 */
const VERSION_LINE_WIDTH = 270;
const VERSION_RESERVED =
    Math.ceil((STATUS_WIDTH + DETAIL_MIN_WIDTH + SUCCESS_WIDTH + DUE_WIDTH + DURATION_WIDTH + 7 * CELL_PADDING + TABLE_BORDER) / 2) + HEADER_SKEW + 2;
const VERSION_MIN_WIDTH = `clamp(0px, calc(50cqw - ${VERSION_RESERVED}px), ${VERSION_LINE_WIDTH}px)`;

/** Makes the table's own width the unit for `VERSION_MIN_WIDTH`. */
const TABLE_FRAME_STYLE = { containerType: 'inline-size', width: '100%', minWidth: 0 } as const;



function Cell({ children, minWidth }: { children: string; minWidth: number }) {
    return (
        <div data-testid="data-health-short-cell" style={{ whiteSpace: 'nowrap', minWidth }}>
            <Typography variant="body-sm" tone="text-secondary">
                {children}
            </Typography>
        </div>
    );
}

/**
 * A version that lists every input ("a as-of 2026-10-11; b 2026-10; ...") renders one source per line. A
 * line may itself wrap (at a space, or inside a long hash) when the window is narrow, so this column never pushes the others off
 * the edge (fe#1093). A single value is one line; null is a dash. Under Latest published, `checked` is the
 * last successful check as one more wrapping line in the same cell (fe#1095), so it never widens the column.
 */
function VersionCell({ value, checked }: { value: string | null | undefined; checked?: string | null }) {
    const lines = value ? value.split(';').map((part) => part.trim()).filter((part) => part.length > 0) : [];
    return (
        <div data-testid="data-health-version-cell" style={{ minWidth: VERSION_MIN_WIDTH }}>
            <BlockStack gap="050">
                {(lines.length > 0 ? lines : [dash]).map((line, index) => (
                    <div key={`${index}:${line}`} data-testid="data-health-version-line" style={wrapLongText}>
                        <Typography variant="body-sm" tone="text-secondary">
                            {line}
                        </Typography>
                    </div>
                ))}
                {checked ? (
                    <div data-testid="data-health-checked" style={wrapLongText}>
                        <Typography variant="body-sm" tone="text-secondary">
                            {checked}
                        </Typography>
                    </div>
                ) : null}
            </BlockStack>
        </div>
    );
}

function columnsFor(nowIso: string): ReadonlyArray<DataTableColumn<HealthRowView>> {
    return [
        {
            key: 'status',
            header: 'Status',
            render: (row) => {
                const look = statusLook(row.status);
                return (
                    <div data-testid="data-health-status-cell" style={{ minWidth: STATUS_WIDTH }}>
                    <InlineStack gap="150" blockAlign="center" wrap>
                        <Badge tone={look.tone} dot>
                            {look.label}
                        </Badge>
                        {row.frozen ? <Badge tone="neutral">Frozen</Badge> : null}
                        {row.kind === 'SILENCE' ? <Badge tone="neutral">Derived row</Badge> : null}
                    </InlineStack>
                    </div>
                );
            },
        },
        {
            key: 'name',
            header: 'Source / table',
            render: (row) => (
                <div data-testid="data-health-row-detail" style={{ minWidth: DETAIL_MIN_WIDTH, maxWidth: DETAIL_MAX_WIDTH, ...wrapLongText }}>
                    <BlockStack gap="050">
                        <Typography variant="body-sm" fontWeight="semibold" testID="data-health-row-name">
                            {row.name}
                        </Typography>
                        <Typography variant="body-sm" tone={row.status === 'RED' ? 'text-critical' : 'text-secondary'}>
                            {rowSubline(row)}
                        </Typography>
                    </BlockStack>
                </div>
            ),
        },
        { key: 'held', header: 'Version held', render: (row) => <VersionCell value={row.versionHeld} /> },
        { key: 'latest', header: 'Latest published', render: (row) => (
                <VersionCell value={row.publisherLatest} checked={checkedLabel(row.publisherLatestAt, nowIso)} />
            ),
        },
        {
            key: 'success',
            header: 'Last success',
            render: (row) => (
                <div data-testid="data-health-short-cell" style={{ whiteSpace: 'nowrap', minWidth: SUCCESS_WIDTH }}>
                    <Typography variant="body-sm" tone="text-secondary">
                        {lastSuccessLabel(row.lastSuccessAt, nowIso)}
                    </Typography>
                    {/* On its own line, so the marker never widens the column (the table is sized to fit). */}
                    {isSeededOutcome(row.lastOutcome) ? (
                        <Typography variant="body-sm" tone="text-secondary" testID="data-health-seeded">
                            seeded
                        </Typography>
                    ) : null}
                </div>
            ),
        },
        { key: 'due', header: 'Next due', render: (row) => <Cell minWidth={DUE_WIDTH}>{nextDueLabel(row.nextDueAt, nowIso)}</Cell> },
        {
            key: 'duration',
            header: 'Duration',
            align: 'end',
            render: (row) => <Cell minWidth={DURATION_WIDTH}>{durationLabel(row.durationSeconds)}</Cell>,
        },
    ];
}

function Panel({
    title,
    testID,
    count,
    children,
}: {
    title: string;
    testID: string;
    count?: number;
    children: React.ReactNode;
}) {
    return (
        <Card padding="400" testID={testID}>
            <BlockStack gap="200">
                <InlineStack gap="200" blockAlign="center">
                    <Typography variant="heading-sm" as="h2" fontWeight="semibold">
                        {title}
                    </Typography>
                    {count !== undefined ? <Badge tone={count > 0 ? 'warning' : 'neutral'}>{String(count)}</Badge> : null}
                </InlineStack>
                {children}
            </BlockStack>
        </Card>
    );
}

/**
 * The Data health page (pv-frontend#1079): a status strip with the red / amber / green counts, the due
 * drops, the expiring credentials and the scheduler's own line, then the table of every row worst first.
 * "No run for 2x the shortest cadence" is an ordinary red row in the table (the server derives it), not a
 * banner. Presentational: the container feeds it the read model.
 */
export function DataHealthPage({ report }: { report: HealthReportView }) {
    const [domain, setDomain] = useState(ALL_DOMAINS);
    const [status, setStatus] = useState<StatusFilter>('ALL');

    const { counts } = report;
    const shown = filterRows(report.rows, domain, status);

    const domainOptions: SelectOption[] = [
        { value: ALL_DOMAINS, label: 'All domains' },
        ...report.domains.map((name) => ({ value: name, label: name })),
    ];
    const statusOptions: SegmentedOption<StatusFilter>[] = [
        { value: 'ALL', label: 'All' },
        { value: 'RED', label: `Red · ${counts.red}` },
        { value: 'AMBER', label: `Amber · ${counts.amber}` },
        { value: 'GREEN', label: `Green · ${counts.green}` },
        { value: 'FROZEN', label: `Frozen · ${counts.frozen}` },
    ];

    return (
        <Box padding="600">
            <BlockStack gap="500">
                <PageHeader
                    title="Data health"
                    description="Every source, external job and synced table the pipeline records in reporting.data_freshness: what we hold, what the publisher has, when it last worked and when it's due."
                />

                <BlockStack gap="300">
                    <InlineStack gap="400" blockAlign="center" wrap>
                        {(
                            [
                                ['red', 'critical', counts.red],
                                ['amber', 'warning', counts.amber],
                                ['green', 'success', counts.green],
                                ['frozen', 'info', counts.frozen],
                            ] as const
                        ).map(([name, tone, value]) => (
                            <InlineStack key={name} gap="100" blockAlign="center">
                                <Box width={vars.space['200']} height={vars.space['200']} borderRadius="full" background={tone} />
                                <Typography
                                    variant="body-md"
                                    fontWeight="semibold"
                                    tone={name === 'frozen' ? 'text-secondary' : undefined}
                                    testID={`data-health-count-${name}`}
                                >
                                    {`${value} ${name}`}
                                </Typography>
                            </InlineStack>
                        ))}
                    </InlineStack>
                    <div aria-hidden="true">
                    <Box testID="data-health-strip">
                    <InlineStack gap="050" wrap>
                        {stripOrder(report.rows).map((row, index) => (
                            <span
                                key={`${row.kind}:${row.name}:${index}`}
                                title={`${row.name}: ${statusLook(row.status).label}${row.frozen ? ' (frozen)' : ''}`}
                                style={{ display: 'inline-flex' }}
                            >
                                <Box
                                    testID={`data-health-strip-cell-${row.frozen && row.status === 'GREEN' ? 'FROZEN' : row.status}`}
                                    width={vars.space['500']}
                                    height={vars.space['500']}
                                    borderRadius="100"
                                    background={stripTone(row.status, row.frozen)}
                                />
                            </span>
                        ))}
                    </InlineStack>
                    </Box>
                    </div>
                </BlockStack>

                <InlineStack gap="400" wrap blockAlign="stretch">
                    <Box flex={1} minWidth={260}>
                        <Panel
                            title="Due drops"
                            testID="data-health-due-drops"
                            count={report.dueDrops.length}
                        >
                            {report.dueDrops.length === 0 ? (
                                <Typography variant="body-sm" tone="text-secondary">
                                    No manual drops are due.
                                </Typography>
                            ) : (
                                report.dueDrops.map((row) => (
                                    <BlockStack key={`${row.kind}:${row.name}`} gap="050">
                                        <Typography variant="body-sm" fontWeight="semibold">
                                            {row.name}
                                        </Typography>
                                        <Typography variant="body-sm" tone="text-secondary">
                                            {rowSubline(row)}
                                        </Typography>
                                    </BlockStack>
                                ))
                            )}
                        </Panel>
                    </Box>
                    <Box flex={1} minWidth={260}>
                        <Panel title="Expiring in 30 days" testID="data-health-expiring">
                            {report.expiring.length > 0 ? (
                                report.expiring.map((expiry) => (
                                    <Typography key={expiry.name} variant="body-sm">
                                        {expiry.expired
                                            ? `${expiry.name} · expired ${shortDate(expiry.expiresOn)}`
                                            : expiryLabel(expiry)}
                                    </Typography>
                                ))
                            ) : (
                                <BlockStack gap="050">
                                    <Typography variant="body-sm" tone="text-secondary">
                                        Nothing expires in the next 30 days.
                                    </Typography>
                                    {report.nextExpiry ? (
                                        <Typography variant="body-sm" tone="text-secondary">
                                            {`Next: ${expiryLabel(report.nextExpiry)}`}
                                        </Typography>
                                    ) : null}
                                </BlockStack>
                            )}
                        </Panel>
                    </Box>
                    <Box flex={1} minWidth={260}>
                        <Panel title="Scheduler" testID="data-health-scheduler">
                            {report.scheduler ? (
                                <SchedulerBody row={report.scheduler} generatedAt={report.generatedAt} liveTier={report.liveTier} />
                            ) : (
                                <Typography variant="body-sm" tone="text-secondary">
                                    The scheduler has not written a health line yet.
                                </Typography>
                            )}
                        </Panel>
                    </Box>
                </InlineStack>

                <InlineStack gap="400" wrap blockAlign="end">
                    <Box minWidth={220}>
                        <Select
                            label="Domain"
                            value={domain}
                            onChange={setDomain}
                            options={domainOptions}
                        />
                    </Box>
                    <BlockStack gap="150">
                        <Typography variant="body-sm" fontWeight="medium" tone="text-secondary">
                            Status
                        </Typography>
                        <SegmentedControl
                            accessibilityLabel="Filter by status"
                            options={statusOptions}
                            value={status}
                            onChange={setStatus}
                        />
                    </BlockStack>
                </InlineStack>

                <BlockStack gap="150">
                    <Typography variant="body-sm" tone="text-secondary" testID="data-health-summary">
                        {`${shown.length} of ${counts.total} rows · worst first${
                            report.lastWrittenAt ? ` · written ${shortDateTime(report.lastWrittenAt)}` : ''
                        }`}
                    </Typography>
                    <div data-testid="data-health-table-frame" style={TABLE_FRAME_STYLE}>
                    <DataTable
                        columns={columnsFor(report.generatedAt)}
                        rows={shown}
                        keyExtractor={(row) => `${row.kind}:${row.name}`}
                        testID="data-health-table"
                        emptyState={
                            report.rows.length === 0
                                ? 'Nothing has been recorded in reporting.data_freshness yet.'
                                : 'No rows match these filters.'
                        }
                    />
                    </div>
                </BlockStack>
            </BlockStack>
        </Box>
    );
}

/** The Scheduler panel body: the tier always (and the live one when it differs), the idle settle time, and never "healthy" for a scheduler that has not ticked. */
function SchedulerBody({ row, generatedAt, liveTier }: { row: HealthRowView; generatedAt: string; liveTier?: string | null }) {
    const view = schedulerView(row, generatedAt, liveTier);
    const neverTickedBadge = view.neverTicked && row.status === 'GREEN';
    const badge = neverTickedBadge ? { label: 'Never ticked', tone: 'neutral' as const } : statusLook(row.status);
    return (
        <BlockStack gap="100">
            <InlineStack gap="200" blockAlign="center" wrap>
                <Badge tone={badge.tone} dot>
                    {badge.label}
                </Badge>
                {neverTickedBadge ? null : (
                    <Typography variant="body-sm" tone="text-secondary" testID="data-health-scheduler-ticks">
                        {row.lastSuccessAt
                            ? `Last good tick ${tickLabel(row.lastSuccessAt, generatedAt)}`
                            : view.neverTicked
                              ? 'Never ticked'
                              : 'No good tick recorded'}
                    </Typography>
                )}
            </InlineStack>
            <Typography variant="body-sm" tone="text-secondary" testID="data-health-scheduler-tier">
                {view.tierLine ? `Database tier: ${view.tierLine}` : 'Database tier: not reported'}
                {view.liveUnread ? ' · live tier not read' : ''}
            </Typography>
            {view.settleLine ? (
                <Typography variant="body-sm" tone="text-secondary" testID="data-health-scheduler-settle">
                    {view.settleLine}
                </Typography>
            ) : null}
            {view.overdueHeadline ? (
                <Typography variant="body-sm" fontWeight="medium" testID="data-health-scheduler-overdue">
                    {view.overdueHeadline}
                </Typography>
            ) : null}
            {view.detail && row.status !== 'GREEN' ? (
                <Typography variant="body-sm" tone="text-secondary" testID="data-health-scheduler-detail">
                    {view.detail}
                </Typography>
            ) : null}
        </BlockStack>
    );
}
